import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { styled } from '@mui/material/styles';
import { Checkbox, FormControl, IconButton } from '@mui/material';
import { type Control, Controller } from 'react-hook-form';
import Input from 'components/Input';
import Icon from 'components/Icon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import NoStylesButton from 'components/NoStylesButton';
import type { QuizQuestionFormInput } from 'features/quizzes/components/quiz-form/QuizQuestionForm';
import Radio from 'components/Radio';

const Root = styled('div')({
  display: 'flex',
  flexWrap: 'nowrap',
  '& .MuiInputBase-root .MuiInputBase-input': {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  '& .MuiCheckbox-root.MuiCheckbox-sizeMedium': {
    padding: '7px',
  },
  '& .MuiRadio-root': {
    padding: '10px',
  },
});

type DynamicChoiceProps = {
  id: string;
  errors: any;
  control: Control<QuizQuestionFormInput>;
  index: number;
  deleteChoice: (index: number) => void;
  multiple?: boolean;
  onChange?: (index: number, checked: boolean) => void;
};

const DragHandle = styled(NoStylesButton, {
  shouldForwardProp: (prop) => prop !== 'isDragging',
})<{ isDragging: boolean }>(({ theme, isDragging }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  touchAction: 'manipulation',
  flexShrink: 0,
  width: '38px',
  cursor: 'grab',
  border: `1px solid ${theme.palette.divider}`,
  borderTopRightRadius: '4px',
  borderBottomRightRadius: '4px',
  borderLeftWidth: 0,
  ...(isDragging && {
    backgroundColor: theme.palette.action.selected,
  }),
}));

export function DynamicChoice({
  id,
  index,
  control,
  errors,
  deleteChoice,
  multiple,
  onChange,
}: DynamicChoiceProps) {
  const { t } = useTranslation('quizzes');

  const {
    isDragging,
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    setActivatorNodeRef,
  } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Root
      ref={setNodeRef}
      style={style}
      sx={[
        isDragging
          ? {
              opacity: 0.5,
            }
          : {
              opacity: 1,
            },
      ]}
    >
      <Controller
        name={`choices.${index}.correct`}
        control={control}
        render={({ field: { value, ref, ...field } }) =>
          multiple ? (
            <Checkbox
              {...field}
              checked={value}
              slotProps={{
                input: {
                  'aria-label': value
                    ? t('quiz_form.questions.choices.uncheck_correct_answer_aria_label', {
                        number: index + 1,
                      })
                    : t('quiz_form.questions.choices.check_correct_answer_aria_label', {
                        number: index + 1,
                      }),
                  'aria-invalid': !!errors?.choices?.[index]?.correct,
                },
              }}
              sx={{
                mr: 1,
              }}
            />
          ) : (
            <Radio
              {...field}
              checked={value}
              onChange={(e) => {
                onChange?.(index, e.target.checked);
              }}
              // color={errors?.choices?.[index]?.correct ? 'error' : 'primary'}
              slotProps={{
                input: {
                  'aria-label': t('quiz_form.questions.choices.check_correct_answer_aria_label', {
                    number: index + 1,
                  }),
                  'aria-invalid': !!errors?.choices?.[index]?.correct,
                },
              }}
              sx={{
                mr: 1,
              }}
            />
          )
        }
      />
      <FormControl fullWidth error={!!errors?.choices?.[index]?.text}>
        <Controller
          name={`choices.${index}.text`}
          control={control}
          render={({ field: { ref, ...field } }) => (
            <Input
              {...field}
              placeholder={t('quiz_form.questions.choices.placeholder')}
              inputRef={ref}
              inputProps={{
                'aria-label': t('quiz_form.questions.choices.input_aria_label', {
                  number: index + 1,
                }),
              }}
            />
          )}
        />
      </FormControl>
      <DragHandle
        {...listeners}
        {...attributes}
        ref={setActivatorNodeRef}
        isDragging={isDragging}
        aria-label={t('quiz_form.questions.choices.drag_choice_aria_label', { number: index + 1 })}
      >
        <Icon
          name="drag"
          sx={{
            fontSize: '32px',
          }}
        />
      </DragHandle>
      <IconButton
        sx={{
          ml: 1,
          p: '2px 3px',
        }}
        onClick={() => deleteChoice(index)}
        aria-label={t('quiz_form.questions.choices.btn_delete_choice', { number: index + 1 })}
      >
        <Icon
          name="close"
          sx={{
            fontSize: '32px',
          }}
        />
      </IconButton>
    </Root>
  );
}
